// ─────────────────────────────────────────────────────────────
//  Provisionsrechner – Vergütungsplan durchspielen, bevor er gilt.
//
//  Zwei Modi:
//    • Frei          – beliebige Preise durchspielen (Angebotskalkulation)
//    • Kunde gewählt – lädt Terminpreis, Provisionsbasis und das real
//                      zugewiesene Team aus der Sales Pipeline
//
//  Die Karte ist bewusst screenshot-tauglich: Der Kundenpreis (und damit
//  unsere Marge) lässt sich ausblenden, wenn der Screenshot an den
//  Outreach-Manager geht.
//
//  Gerechnet wird ausschließlich über commissionCore (via paymentEngine) –
//  dieselbe Logik, die später auch die echte Abrechnung erzeugt.
// ─────────────────────────────────────────────────────────────
import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { staggerContainer, itemVariants } from './dashboardMotion';
import { database } from '../firebaseConfig';
import { ref, onValue } from 'firebase/database';
import {
  COMMISSION_ROLES,
  COMMISSION_ROLE_IDS,
  COMMISSION_PLAN_BASE,
  commissionBreakdown,
  commissionRoleForMember,
  customerRoleOverride,
  memberCommissionMinimum,
  commissionBaseFor,
  appointmentPriceFor,
  fmtEur,
} from './paymentEngine';
import './CommissionCalculator.css';

const CUSTOMERS_PATH = 'salesgod-research/customers';
const TEAM_PATH = 'salesgod-research/team';

const PRICE_MIN = 100;
const PRICE_MAX = 600;
const COUNT_MAX = 60;

const num = (s) => {
  const n = Number(String(s).replace(/[^\d.,-]/g, '').replace(/\./g, '').replace(',', '.'));
  return isNaN(n) ? 0 : n;
};
const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));
const fillPct = (v, lo, hi) => `${((v - lo) / (hi - lo)) * 100}%`;
const pct1 = (n) => `${(Math.round(n * 10) / 10).toLocaleString('de-DE')} %`;

const rangeLabel = (t) => (t.to == null ? `${t.from}+` : `${t.from}–${t.to}`);

const CommissionCalculator = () => {
  const [customers, setCustomers] = useState({});
  const [team, setTeam] = useState({});
  const [slug, setSlug] = useState('');
  const [price, setPrice] = useState(390);
  const [base, setBase] = useState(260);
  const [count, setCount] = useState(15);
  const [minimums, setMinimums] = useState(() =>
    COMMISSION_ROLE_IDS.reduce((acc, id) => ({ ...acc, [id]: String(COMMISSION_ROLES[id].defaultMinimum) }), {})
  );
  const [showPrice, setShowPrice] = useState(true);

  useEffect(() => {
    const unsub = onValue(ref(database, CUSTOMERS_PATH), (snap) => setCustomers(snap.val() || {}));
    return () => unsub();
  }, []);
  useEffect(() => {
    const unsub = onValue(ref(database, TEAM_PATH), (snap) => setTeam(snap.val() || {}));
    return () => unsub();
  }, []);

  const customer = slug ? customers[slug] : null;
  const contract = customer ? customer.contract : null;

  // Kunden mit Vertrag – nur die sind für eine echte Abrechnung relevant.
  const customerOptions = useMemo(
    () => Object.keys(customers)
      .filter((s) => customers[s] && customers[s].contract)
      .map((s) => ({ slug: s, name: customers[s].companyName || s, active: !!customers[s].contract.active }))
      .sort((a, b) => Number(b.active) - Number(a.active) || a.name.localeCompare(b.name)),
    [customers]
  );

  // Kundenwahl übernimmt die intern festgelegten Beträge des Vertrags. Bewusst
  // an den Zahlen statt am Vertragsobjekt hängen – Firebase liefert bei jedem
  // Snapshot ein neues Objekt und würde sonst laufend die Regler zurücksetzen.
  const contractPrice = contract ? Math.round(appointmentPriceFor(contract, 0)) : 0;
  const contractBase = contract ? Math.round(commissionBaseFor(contract)) : 0;
  useEffect(() => {
    if (contractPrice > 0) setPrice(clamp(contractPrice, PRICE_MIN, PRICE_MAX));
    if (contractBase > 0) setBase(clamp(contractBase, 0, PRICE_MAX));
  }, [slug, contractPrice, contractBase]);

  // Teilnehmer der Abrechnung: im Kundenmodus das real zugewiesene Team mit
  // seinen echten Minimums, sonst je ein Platzhalter pro Staffel.
  const participants = useMemo(() => {
    if (customer) {
      const assigned = customer.team || {};
      return Object.keys(assigned)
        .map((id) => ({ id, member: team[id] }))
        .filter((x) => x.member)
        .map(({ id, member }) => {
          const roleId = commissionRoleForMember(member, customerRoleOverride(customer, id));
          if (!roleId) return null;
          return {
            id,
            name: member.name || COMMISSION_ROLES[roleId].label,
            roleId,
            minimum: memberCommissionMinimum(member, roleId),
          };
        })
        .filter(Boolean)
        .sort((a, b) => COMMISSION_ROLE_IDS.indexOf(a.roleId) - COMMISSION_ROLE_IDS.indexOf(b.roleId));
    }
    return COMMISSION_ROLE_IDS.map((roleId) => ({
      id: roleId,
      name: COMMISSION_ROLES[roleId].label,
      roleId,
      minimum: Math.max(0, num(minimums[roleId])),
    }));
  }, [customer, team, minimums]);

  const result = useMemo(
    () => commissionBreakdown({ appointments: count, appointmentPrice: price, commissionBase: base, participants }),
    [count, price, base, participants]
  );

  // Welche Staffeln zeigen wir unten im Detail? Genau die, die hier mitspielen.
  const shownRoles = useMemo(() => {
    const ids = participants.map((p) => p.roleId);
    return COMMISSION_ROLE_IDS.filter((id) => ids.includes(id));
  }, [participants]);

  const roleScale = (roleId) => (base / COMMISSION_PLAN_BASE) * COMMISSION_ROLES[roleId].factor;

  const baseAbovePrice = base > price;

  return (
    <div className="cc-wrap">
      <motion.div className="cc-inner" variants={staggerContainer} initial="initial" animate="animate">
        <motion.header className="cc-head" variants={itemVariants}>
          <div>
            <div className="cc-eyebrow">BonusClub · Vergütungsplan</div>
            <h1>Provisionsrechner</h1>
            <p>
              Lege fest, was der Kunde pro Termin zahlt und wie viel davon als Umsatz in die Provision fließt.
              Der Rechner zeigt sofort, was Outreach-Manager und Teamleiter bei X Terminen verdienen – und was als Marge übrig bleibt.
            </p>
          </div>
        </motion.header>

        {/* ── Kunde wählen ── */}
        <motion.section className="cc-picker" variants={itemVariants}>
          <label className="cc-field cc-field-grow">
            <span>Kunde</span>
            <select className="cc-select" value={slug} onChange={(e) => setSlug(e.target.value)}>
              <option value="">Freie Kalkulation (kein Kunde)</option>
              {customerOptions.map((c) => (
                <option key={c.slug} value={c.slug}>{c.name}{c.active ? '' : ' · pausiert'}</option>
              ))}
            </select>
          </label>
          <p className="cc-picker-note">
            {customer
              ? (participants.length > 0
                  ? `Beträge und Team kommen aus dem Vertrag von ${customer.companyName || slug}.`
                  : `${customer.companyName || slug} hat noch kein Teammitglied mit Provisionsstaffel zugewiesen.`)
              : 'Ohne Kundenwahl rechnest du frei – ideal, um vor dem Abschluss einen Preis zu bestimmen.'}
          </p>
        </motion.section>

        {/* ── Stellschrauben ── */}
        <motion.section className="cc-controls" variants={itemVariants}>
          <div className="cc-control">
            <div className="cc-control-top">
              <span className="cc-control-label">Kunde zahlt / Termin</span>
              <span className="cc-control-value">{fmtEur(price)}</span>
            </div>
            <input
              type="range" min={PRICE_MIN} max={PRICE_MAX} step={10} value={price}
              style={{ '--cc-fill': fillPct(price, PRICE_MIN, PRICE_MAX) }}
              onChange={(e) => setPrice(Number(e.target.value))}
            />
            <div className="cc-range-minmax"><span>{fmtEur(PRICE_MIN)}</span><span>{fmtEur(PRICE_MAX)}</span></div>
          </div>

          <div className="cc-control">
            <div className="cc-control-top">
              <span className="cc-control-label">Davon Umsatz für die Provision</span>
              <span className="cc-control-value">{fmtEur(base)}</span>
            </div>
            <input
              type="range" min={0} max={PRICE_MAX} step={10} value={base}
              style={{ '--cc-fill': fillPct(base, 0, PRICE_MAX) }}
              onChange={(e) => setBase(Number(e.target.value))}
            />
            <div className="cc-range-minmax">
              <span>0 €</span>
              <span className={baseAbovePrice ? 'cc-warn' : ''}>
                {baseAbovePrice ? 'über dem Kundenpreis' : `${fmtEur(price - base)} nicht provisionsfähig`}
              </span>
            </div>
          </div>

          <div className="cc-control">
            <div className="cc-control-top">
              <span className="cc-control-label">Anzahl Termine</span>
              <span className="cc-control-value">{count}{count >= COUNT_MAX ? '+' : ''}</span>
            </div>
            <input
              type="range" min={0} max={COUNT_MAX} step={1} value={count}
              style={{ '--cc-fill': fillPct(count, 0, COUNT_MAX) }}
              onChange={(e) => setCount(Number(e.target.value))}
            />
            <div className="cc-range-minmax"><span>0</span><span>{COUNT_MAX}+</span></div>
          </div>

          {!customer && COMMISSION_ROLE_IDS.map((roleId) => (
            <div className="cc-control cc-control-min" key={roleId}>
              <div className="cc-control-top">
                <span className="cc-control-label">Minimum {COMMISSION_ROLES[roleId].label}</span>
              </div>
              <div className="cc-input-suffix">
                <span className="cc-currency">€</span>
                <input
                  type="text" inputMode="numeric" value={minimums[roleId]}
                  onChange={(e) => setMinimums((p) => ({ ...p, [roleId]: e.target.value }))}
                />
                <span className="cc-hint">pro Abrechnung</span>
              </div>
            </div>
          ))}
        </motion.section>

        {/* ── Teilbare Karte ── */}
        <motion.div className="cc-share-tag" variants={itemVariants}>Zum Teilen / Screenshot</motion.div>

        <motion.label className="cc-privacy" variants={itemVariants}>
          <span className="cc-privacy-text">
            Kundenpreis, Umsatz &amp; Marge <b>anzeigen</b>
            <br />Aus lassen, wenn der Screenshot an den Outreach-Manager geht.
          </span>
          <span className="cc-switch">
            <input type="checkbox" checked={showPrice} onChange={(e) => setShowPrice(e.target.checked)} />
            <span className="cc-switch-track"><span className="cc-switch-thumb" /></span>
          </span>
        </motion.label>

        <motion.section className={`cc-card ${showPrice ? '' : 'cc-card-private'}`} variants={itemVariants}>
          <div className="cc-card-head">
            <div className="cc-card-title">
              {customer ? (customer.companyName || slug) : 'BonusClub'} <span>Auszahlung</span>
            </div>
            <div className="cc-card-meta">
              {showPrice && <><b>{fmtEur(price)}</b> / Termin · </>}
              <b>{count}</b> {count === 1 ? 'Termin' : 'Termine'}
            </div>
          </div>

          {showPrice && (
            <div className="cc-revenue">
              <div>
                <span className="cc-revenue-label">Umsatz gesamt</span>
                <div className="cc-revenue-sub">
                  Provisionsfähig: <b>{fmtEur(result.commissionRevenue)}</b>
                  {result.nonCommissionable > 0 && <span> · {fmtEur(result.nonCommissionable)} nicht provisionsfähig</span>}
                </div>
              </div>
              <span className="cc-revenue-value">{fmtEur(result.customerRevenue)}</span>
            </div>
          )}

          {result.shares.length === 0 ? (
            <p className="cc-empty">
              Kein Teammitglied mit Provisionsstaffel. Ordne im Tab <strong>Team &amp; Provisionen</strong> eine Staffel zu.
            </p>
          ) : (
            <div className="cc-earners">
              {result.shares.map((s, i) => (
                <div className="cc-earner" key={s.id}>
                  <div className="cc-earner-head">
                    <span className="cc-earner-badge">{i + 1}</span>
                    <span className="cc-earner-name">{s.name}</span>
                    <span className="cc-earner-role">{s.roleLabel}</span>
                  </div>
                  <div className="cc-earner-value">{fmtEur(s.amount)}</div>
                  <div className="cc-earner-break">
                    Staffel <b>{fmtEur(s.tierAmount)}</b> · Unlocks <b>{fmtEur(s.unlockAmount)}</b>
                    {s.minimumApplied && <span className="cc-tag-min">Minimum {fmtEur(s.minimum)}</span>}
                  </div>
                  <div className="cc-earner-rate">
                    {count > 0 ? `aktuell ${fmtEur(s.ratePerAppointment)} / Termin` : 'noch keine Termine'}
                  </div>
                </div>
              ))}
            </div>
          )}

          {showPrice && result.customerRevenue > 0 && (
            <>
              <div className="cc-split">
                <div className="cc-split-label">
                  <span>Aufteilung vom Umsatz</span>
                  <span>{pct1(result.companyPct)} Firma</span>
                </div>
                <div className="cc-split-bar">
                  <span className="cc-seg cc-seg-firma" style={{ width: `${Math.max(0, result.companyPct)}%` }} />
                  {result.shares.map((s) => (
                    <span
                      key={s.id}
                      className={`cc-seg cc-seg-${s.roleId}`}
                      style={{ width: `${(s.amount / result.customerRevenue) * 100}%` }}
                    />
                  ))}
                </div>
                <div className="cc-legend">
                  <span><i className="cc-dot cc-dot-firma" />Firma</span>
                  {shownRoles.map((roleId) => (
                    <span key={roleId}><i className={`cc-dot cc-dot-${roleId}`} />{COMMISSION_ROLES[roleId].label}</span>
                  ))}
                </div>
              </div>

              <div className="cc-margin">
                <div>
                  <div className="cc-margin-label">Marge Firma</div>
                  <div className="cc-margin-sub">{fmtEur(result.companyAmount)} übrig · {fmtEur(result.payoutTotal)} ans Team</div>
                </div>
                <div className={`cc-margin-value ${result.companyAmount < 0 ? 'cc-margin-neg' : ''}`}>{pct1(result.companyPct)}</div>
              </div>
            </>
          )}

          {!showPrice && (
            <p className="cc-private-note">Kundenpreis, Umsatz &amp; Firmenmarge sind in dieser Ansicht ausgeblendet.</p>
          )}
        </motion.section>

        <motion.p className="cc-foot" variants={itemVariants}>
          Der Vergütungsplan ist auf {fmtEur(COMMISSION_PLAN_BASE)} pro Termin kalibriert und wird proportional auf die
          eingestellte Provisionsbasis skaliert. Genau diese Rechnung erzeugt später auch die echte Monatsabrechnung.
        </motion.p>

        {/* ── Staffelung im Detail ── */}
        {shownRoles.length > 0 && (
          <>
            <motion.div className="cc-tiers-head" variants={itemVariants}><h2>Staffelung im Detail</h2></motion.div>
            <motion.div className="cc-tiers" variants={itemVariants}>
              {shownRoles.map((roleId) => {
                const role = COMMISSION_ROLES[roleId];
                const scale = roleScale(roleId);
                return (
                  <div className="cc-tier-card" key={roleId}>
                    <div className="cc-tier-card-head">
                      <span className={`cc-dot cc-dot-${roleId}`} />
                      <span className="cc-tier-card-title">{role.label}</span>
                    </div>
                    <table className="cc-tier-table">
                      <thead>
                        <tr><th>{role.tierHeading}</th><th>Satz / Termin</th></tr>
                      </thead>
                      <tbody>
                        {role.tiers.map((t) => {
                          const active = count >= t.from && (t.to == null || count <= t.to);
                          return (
                            <tr key={`${t.from}-${t.to}`} className={active ? 'cc-tier-active' : ''}>
                              <td>{rangeLabel(t)}</td>
                              <td>{fmtEur(t.rate * scale)}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                    <div className="cc-unlock-head">{role.unlockHeading}</div>
                    <div className="cc-unlock-list">
                      {role.unlocks.map((u) => (
                        <div key={u.at} className={`cc-unlock ${count >= u.at ? 'cc-unlock-on' : ''}`}>
                          <span>ab {u.at} Terminen</span>
                          <span className="cc-unlock-val">{count >= u.at ? '✓ ' : ''}+{fmtEur(u.bonus * scale)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </motion.div>
          </>
        )}
      </motion.div>
    </div>
  );
};

export default CommissionCalculator;
